import type { IconOption } from '@/components/IconPicker'

export interface IconGroup {
  group: string
  icons: IconOption[]
}

// The full set every IconPicker offers beneath its context's suggestions (categories,
// accounts, bills, subscriptions). Every `name` must be a valid lucide-react dynamic icon
// name and appear only once across all groups — iconCatalog.test.ts checks both. Still
// hand-picked rather than all ~1900 lucide icons: a searchable few hundred covers real
// household labels without burying them under developer/UI glyphs.
export const ICON_CATALOG: IconGroup[] = [
  {
    group: 'Money & Banking',
    icons: [
      { name: 'landmark', label: 'Bank' },
      { name: 'wallet', label: 'Wallet' },
      { name: 'wallet-cards', label: 'Cards Wallet' },
      { name: 'banknote', label: 'Cash' },
      { name: 'coins', label: 'Coins' },
      { name: 'indian-rupee', label: 'Rupee' },
      { name: 'piggy-bank', label: 'Savings' },
      { name: 'vault', label: 'Locker / Vault' },
      { name: 'credit-card', label: 'Credit Card' },
      { name: 'hand-coins', label: 'Lent / Receivable' },
      { name: 'handshake', label: 'Deal / Agreement' },
      { name: 'receipt', label: 'Bills' },
      { name: 'receipt-indian-rupee', label: 'Rupee Receipt' },
      { name: 'file-text', label: 'Document' },
      { name: 'trending-up', label: 'Investments' },
      { name: 'trending-down', label: 'Losses' },
      { name: 'line-chart', label: 'Stocks / Mutual Funds' },
      { name: 'chart-pie', label: 'Portfolio' },
      { name: 'chart-column', label: 'Reports' },
      { name: 'gem', label: 'Gold / Jewellery' },
      { name: 'percent', label: 'Interest / Tax' },
      { name: 'calculator', label: 'Accounting' },
      { name: 'scale', label: 'Legal' },
      { name: 'shield-check', label: 'Insurance' },
      { name: 'umbrella', label: 'Cover / Protection' },
      { name: 'badge-percent', label: 'Discount / Offer' },
      { name: 'arrow-left-right', label: 'Transfer' },
      { name: 'repeat', label: 'Recurring' },
      { name: 'qr-code', label: 'UPI / QR' },
      { name: 'lock-keyhole', label: 'Safe' },
    ],
  },
  {
    group: 'Food & Drink',
    icons: [
      { name: 'utensils', label: 'Food & Dining' },
      { name: 'utensils-crossed', label: 'Restaurant' },
      { name: 'chef-hat', label: 'Cooking' },
      { name: 'cooking-pot', label: 'Kitchen' },
      { name: 'soup', label: 'Meals' },
      { name: 'salad', label: 'Healthy Food' },
      { name: 'sandwich', label: 'Snacks' },
      { name: 'pizza', label: 'Pizza' },
      { name: 'hamburger', label: 'Fast Food' },
      { name: 'coffee', label: 'Coffee & Tea' },
      { name: 'cup-soda', label: 'Soft Drinks' },
      { name: 'milk', label: 'Milk / Dairy' },
      { name: 'egg', label: 'Eggs' },
      { name: 'beef', label: 'Meat' },
      { name: 'fish', label: 'Fish' },
      { name: 'drumstick', label: 'Chicken' },
      { name: 'carrot', label: 'Vegetables' },
      { name: 'apple', label: 'Fruits' },
      { name: 'wheat', label: 'Grains / Atta' },
      { name: 'croissant', label: 'Bakery' },
      { name: 'cake', label: 'Cake' },
      { name: 'cookie', label: 'Sweets' },
      { name: 'ice-cream-cone', label: 'Ice Cream' },
      { name: 'candy', label: 'Candy' },
      { name: 'wine', label: 'Drinks' },
      { name: 'beer', label: 'Beer' },
    ],
  },
  {
    group: 'Shopping',
    icons: [
      { name: 'shopping-cart', label: 'Groceries' },
      { name: 'shopping-bag', label: 'Shopping' },
      { name: 'shopping-basket', label: 'Basket' },
      { name: 'store', label: 'Store' },
      { name: 'package', label: 'Online Orders' },
      { name: 'truck', label: 'Delivery' },
      { name: 'shirt', label: 'Clothing' },
      { name: 'footprints', label: 'Footwear' },
      { name: 'watch', label: 'Watch' },
      { name: 'glasses', label: 'Eyewear' },
      { name: 'backpack', label: 'Bags' },
      { name: 'sofa', label: 'Furniture' },
      { name: 'lamp', label: 'Decor' },
      { name: 'refrigerator', label: 'Appliances' },
      { name: 'washing-machine', label: 'Washing Machine' },
      { name: 'microwave', label: 'Microwave' },
      { name: 'air-vent', label: 'AC' },
      { name: 'fan', label: 'Fan / Cooler' },
      { name: 'tag', label: 'Other' },
      { name: 'tags', label: 'Sale' },
      { name: 'ticket', label: 'Tickets' },
    ],
  },
  {
    group: 'Home & Utilities',
    icons: [
      { name: 'home', label: 'Rent / Home' },
      { name: 'house-plus', label: 'New Home' },
      { name: 'building', label: 'Property' },
      { name: 'building-2', label: 'Society / Apartment' },
      { name: 'key', label: 'Keys / Deposit' },
      { name: 'bed', label: 'Bedroom' },
      { name: 'bath', label: 'Bathroom' },
      { name: 'zap', label: 'Electricity' },
      { name: 'plug', label: 'Power / Inverter' },
      { name: 'battery-charging', label: 'Battery' },
      { name: 'sun', label: 'Solar' },
      { name: 'droplet', label: 'Water' },
      { name: 'flame', label: 'Gas / LPG' },
      { name: 'wifi', label: 'Internet' },
      { name: 'router', label: 'Broadband' },
      { name: 'phone', label: 'Phone / Landline' },
      { name: 'smartphone', label: 'Mobile' },
      { name: 'tv', label: 'TV / DTH' },
      { name: 'wrench', label: 'Repairs' },
      { name: 'hammer', label: 'Renovation' },
      { name: 'paint-roller', label: 'Painting' },
      { name: 'drill', label: 'Tools' },
      { name: 'brush-cleaning', label: 'Cleaning' },
      { name: 'trash-2', label: 'Waste' },
      { name: 'shield', label: 'Security' },
      { name: 'cctv', label: 'CCTV' },
      { name: 'user-cog', label: 'Domestic Help' },
    ],
  },
  {
    group: 'Transport & Travel',
    icons: [
      { name: 'car', label: 'Car' },
      { name: 'car-taxi-front', label: 'Taxi / Cab' },
      { name: 'fuel', label: 'Fuel' },
      { name: 'bus', label: 'Bus' },
      { name: 'train-front', label: 'Train' },
      { name: 'tram-front', label: 'Metro' },
      { name: 'bike', label: 'Bicycle' },
      { name: 'motorbike', label: 'Two-Wheeler' },
      { name: 'plane', label: 'Flights' },
      { name: 'ship', label: 'Cruise / Ferry' },
      { name: 'parking-meter', label: 'Parking' },
      { name: 'circle-parking', label: 'Parking Lot' },
      { name: 'map', label: 'Trips' },
      { name: 'map-pin', label: 'Places' },
      { name: 'navigation', label: 'Commute' },
      { name: 'luggage', label: 'Luggage' },
      { name: 'hotel', label: 'Hotel' },
      { name: 'tent', label: 'Camping' },
      { name: 'mountain', label: 'Mountains' },
      { name: 'palmtree', label: 'Holiday' },
      { name: 'globe', label: 'International' },
      { name: 'stamp', label: 'Passport / Visa' },
      { name: 'car-front', label: 'Vehicle' },
      { name: 'cog', label: 'Servicing' },
    ],
  },
  {
    group: 'Health & Fitness',
    icons: [
      { name: 'heart-pulse', label: 'Health' },
      { name: 'stethoscope', label: 'Doctor' },
      { name: 'hospital', label: 'Hospital' },
      { name: 'pill', label: 'Medicine' },
      { name: 'syringe', label: 'Vaccination' },
      { name: 'thermometer', label: 'Illness' },
      { name: 'activity', label: 'Checkup' },
      { name: 'microscope', label: 'Lab Tests' },
      { name: 'ambulance', label: 'Emergency' },
      { name: 'eye', label: 'Eye Care' },
      { name: 'smile', label: 'Dental' },
      { name: 'brain', label: 'Mental Health' },
      { name: 'dumbbell', label: 'Gym / Fitness' },
      { name: 'medal', label: 'Events / Races' },
      { name: 'trophy', label: 'Sports' },
      { name: 'volleyball', label: 'Games' },
      { name: 'flower-2', label: 'Yoga / Wellness' },
      { name: 'scissors', label: 'Salon' },
      { name: 'sparkles', label: 'Beauty / Spa' },
      { name: 'spray-can', label: 'Cosmetics' },
    ],
  },
  {
    group: 'Family & People',
    icons: [
      { name: 'users', label: 'Family' },
      { name: 'user', label: 'Person' },
      { name: 'user-round', label: 'Self' },
      { name: 'baby', label: 'Baby' },
      { name: 'school', label: 'School' },
      { name: 'toy-brick', label: 'Toys' },
      { name: 'heart', label: 'Partner' },
      { name: 'heart-handshake', label: 'Support Parents' },
      { name: 'hand-heart', label: 'Charity / Donation' },
      { name: 'hand-helping', label: 'Help / Favours' },
      { name: 'party-popper', label: 'Celebrations' },
      { name: 'cake-slice', label: 'Birthday' },
      { name: 'gift', label: 'Gifts' },
      { name: 'ribbon', label: 'Wedding' },
      { name: 'church', label: 'Place of Worship' },
      { name: 'flame-kindling', label: 'Pooja / Rituals' },
      { name: 'sparkle', label: 'Diwali / Festivals' },
      { name: 'paw-print', label: 'Pets' },
      { name: 'dog', label: 'Dog' },
      { name: 'cat', label: 'Cat' },
      { name: 'bird', label: 'Bird' },
      { name: 'contact', label: 'Contacts' },
    ],
  },
  {
    group: 'Education & Work',
    icons: [
      { name: 'graduation-cap', label: 'Education' },
      { name: 'book-open', label: 'Books' },
      { name: 'notebook-pen', label: 'Stationery' },
      { name: 'pencil', label: 'Writing' },
      { name: 'library', label: 'Library' },
      { name: 'languages', label: 'Languages' },
      { name: 'award', label: 'Certification' },
      { name: 'briefcase', label: 'Work / Business' },
      { name: 'briefcase-business', label: 'Office' },
      { name: 'warehouse', label: 'Warehouse' },
      { name: 'factory', label: 'Factory' },
      { name: 'tractor', label: 'Agriculture' },
      { name: 'sprout', label: 'Farming' },
      { name: 'presentation', label: 'Meetings' },
      { name: 'printer', label: 'Printing' },
      { name: 'mail', label: 'Post / Courier' },
      { name: 'id-card', label: 'ID / KYC' },
      { name: 'clipboard-list', label: 'Fees / Forms' },
      { name: 'badge-check', label: 'Verified' },
      { name: 'hard-hat', label: 'Contractor' },
    ],
  },
  {
    group: 'Entertainment & Hobbies',
    icons: [
      { name: 'clapperboard', label: 'Streaming / OTT' },
      { name: 'film', label: 'Movies' },
      { name: 'popcorn', label: 'Cinema' },
      { name: 'music', label: 'Music' },
      { name: 'headphones', label: 'Audio' },
      { name: 'mic', label: 'Podcasts' },
      { name: 'radio', label: 'Radio' },
      { name: 'gamepad-2', label: 'Gaming' },
      { name: 'dices', label: 'Board Games' },
      { name: 'puzzle', label: 'Puzzles' },
      { name: 'palette', label: 'Art' },
      { name: 'camera', label: 'Photography' },
      { name: 'guitar', label: 'Instruments' },
      { name: 'drama', label: 'Theatre' },
      { name: 'newspaper', label: 'News / Magazines' },
      { name: 'book-headphones', label: 'Audiobooks' },
      { name: 'flower', label: 'Gardening' },
      { name: 'trees', label: 'Outdoors' },
      { name: 'fish-symbol', label: 'Fishing' },
      { name: 'star', label: 'Favourites' },
    ],
  },
  {
    group: 'Tech & Subscriptions',
    icons: [
      { name: 'laptop', label: 'Software / Laptop' },
      { name: 'monitor', label: 'Computer' },
      { name: 'tablet', label: 'Tablet' },
      { name: 'cloud', label: 'Cloud Storage' },
      { name: 'hard-drive', label: 'Storage' },
      { name: 'cpu', label: 'Hardware' },
      { name: 'keyboard', label: 'Accessories' },
      { name: 'mouse', label: 'Mouse' },
      { name: 'gamepad', label: 'Console' },
      { name: 'app-window', label: 'Apps' },
      { name: 'globe-lock', label: 'VPN' },
      { name: 'key-round', label: 'Passwords' },
      { name: 'mail-plus', label: 'Email' },
      { name: 'message-circle', label: 'Messaging' },
      { name: 'video', label: 'Video Calls' },
      { name: 'bot', label: 'AI Tools' },
      { name: 'crown', label: 'Premium' },
      { name: 'badge', label: 'Membership' },
      { name: 'calendar-sync', label: 'Renewal' },
      { name: 'bell', label: 'Reminders' },
    ],
  },
  {
    group: 'Symbols',
    icons: [
      { name: 'circle', label: 'Circle' },
      { name: 'square', label: 'Square' },
      { name: 'triangle', label: 'Triangle' },
      { name: 'hexagon', label: 'Hexagon' },
      { name: 'diamond', label: 'Diamond' },
      { name: 'flag', label: 'Flag' },
      { name: 'bookmark', label: 'Bookmark' },
      { name: 'pin', label: 'Pinned' },
      { name: 'target', label: 'Goal' },
      { name: 'rocket', label: 'Big Plans' },
      { name: 'lightbulb', label: 'Ideas' },
      { name: 'leaf', label: 'Eco' },
      { name: 'moon', label: 'Night' },
      { name: 'snowflake', label: 'Winter' },
      { name: 'cloud-rain', label: 'Monsoon' },
      { name: 'smile-plus', label: 'Happy' },
      { name: 'thumbs-up', label: 'Good' },
      { name: 'circle-alert', label: 'Important' },
      { name: 'circle-help', label: 'Unknown' },
      { name: 'calendar', label: 'Calendar' },
      { name: 'clock', label: 'Time' },
      { name: 'hourglass', label: 'Waiting' },
      { name: 'lock', label: 'Locked' },
      { name: 'archive', label: 'Archive' },
      { name: 'box', label: 'Box' },
      { name: 'infinity', label: 'Forever' },
    ],
  },
]

export const ALL_CATALOG_ICON_NAMES: string[] = ICON_CATALOG.flatMap((g) =>
  g.icons.map((i) => i.name)
)
